import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FC } from 'react';

import { AnalyticsProvider } from '@bonusly/analytics';
import { ApplicationConfig, ApplicationProvider } from '@bonusly/application';
import { UserChannelEventProvider } from '@bonusly/channels';
import { CompanyThemeProvider, CustomThemeInjector, ThemeProvider } from '@bonusly/design-system';
import { QueryProvider } from '@bonusly/query-client';
import { AuthProvider } from '@bonusly/who-am-i';

import { ErrorBoundary } from '../components/error/error-boundary';
import { TranslationProvider } from '../components/i18n/translation-provider';
import { NavFallback } from '../components/routes/statuses/NavFallback';
import { SeasonalEffects, SeasonalThemeOverrides, SeasonalThemeProvider } from '../components/seasonal';
import { TelemetryProvider } from '../components/telemetry/telemetry-provider';
import { AppearancePreferencesSyncProvider } from '../components/theme/appearance-preferences-sync';
import { VersionBanner } from '../components/version-banner';
import { AppEvents } from './app-events';
import { AppHttpClients } from './app-http-clients';
import { AppLoading } from './app-loading';
import { AppRouter } from './app-router';

const config: ApplicationConfig = {
	algoliaClientId: import.meta.env.REACT_APP_ALGOLIA_CLIENT_ID,
	datadogApplicationId: import.meta.env.REACT_APP_DATADOG_APPLICATION_ID,
	datadogClientToken: import.meta.env.REACT_APP_DATADOG_CLIENT_TOKEN,
	intercomApiBase: import.meta.env.REACT_APP_INTERCOM_API_BASE,
	intercomAppId: import.meta.env.REACT_APP_INTERCOM_APP_ID,
	monarchOrigin: import.meta.env.REACT_APP_MONARCH_ORIGIN,
	recognizeCable: import.meta.env.REACT_APP_RECOGNIZE_CABLE,
	recognizeOrigin: import.meta.env.REACT_APP_RECOGNIZE_ORIGIN,
	stripePublishableKey: import.meta.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
	translationsOrigin: import.meta.env.REACT_APP_TRANSLATIONS_ORIGIN,
};

const env = import.meta.env.REACT_APP_ENV;

export const App: FC = () => {
	return (
		// Configurage shared application ENV variables
		<ApplicationProvider env={env} {...config}>
			{/* Initialize telemetry and error logging */}
			<TelemetryProvider>
				{/* Initialize translations client and fetch global locale files*/}
				<TranslationProvider>
					{/* Initialize Chakra and design system theme and CSS variables */}
					<ThemeProvider>
						{/* Seasonal theme management (feature flag, localStorage) */}
						<SeasonalThemeProvider>
							{/* Seasonal color mode + accent color overrides via CSS variables */}
							<SeasonalThemeOverrides />
							{/* Custom theme CSS variable overrides from user preferences */}
							<CustomThemeInjector />
							{/* Create top level async query cache */}
							<QueryProvider>
								{/* Set up shared application HTTP clients */}
								<AppHttpClients>
									{/* Fetch JWT using local cookie session, identify user, and fetch feature flags  */}
									<AuthProvider>
										{/* Capture propagated events from inline attributes */}
										<AnalyticsProvider>
											{/* Top level error boundary to catch any exceptions not handled in MFEs */}
											<ErrorBoundary fallback={NavFallback}>
												{/* Wait for authentication to complete and show loading screen during wait */}
												<AppLoading>
													{/* Apply company-wide accent color via Chakra theme overrides */}
													<CompanyThemeProvider>
														{/* Connect general user events channel to global event dispatch */}
														<UserChannelEventProvider>
															{/* Sync user's color mode preference between localStorage and backend */}
															<AppearancePreferencesSyncProvider>
																{/* Display the specified MFE based on the viewed browser path */}
																<AppRouter />
																{/* Initializes global listeners for system-wide events */}
																<AppEvents />
																{/* Version mismatch banner for stale frontend detection */}
																<VersionBanner />
																{/* Seasonal effects overlay (falling leaves, etc.) */}
																<SeasonalEffects />
																<ReactQueryDevtools />
															</AppearancePreferencesSyncProvider>
														</UserChannelEventProvider>
													</CompanyThemeProvider>
												</AppLoading>
											</ErrorBoundary>
										</AnalyticsProvider>
									</AuthProvider>
								</AppHttpClients>
							</QueryProvider>
						</SeasonalThemeProvider>
					</ThemeProvider>
				</TranslationProvider>
			</TelemetryProvider>
		</ApplicationProvider>
	);
};
